import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";

/**
 * Export Cash/Bank Book data to Excel
 * @param {Object} params
 * @param {Array} params.groupedData - data grouped by acct_name
 * @param {string} params.fromDate - report start date (string)
 * @param {string} params.toDate - report end date (string)
 * @param {string} params.societyName - society / company name
 */
export const exportLedgerExcel = ({ groupedData, fromDate, toDate, societyName }) => {
    const wb = XLSX.utils.book_new();
    const wsData = [];

    const formatDate = (isoDate) => {
        if (!isoDate) return "";
        const [year, month, day] = isoDate.split("-");
        return `${day}/${month}/${year}`;
    };

  const formatCurrency = (amount) => {
    const numberFormat = new Intl.NumberFormat('en-IN', {
      style: 'decimal', //'currency',
      minimumFractionDigits: 2,
      currency: 'INR',
      currencyDisplay: 'symbol',
    });
    return numberFormat.format(amount);
  }

    // --- Add report title ---
    wsData.push([`${societyName}`]);
    wsData.push([]); // empty row for spacing
    wsData.push([`Ledger Report For the Period: ${formatDate(fromDate)} To ${formatDate(toDate)}`]);
    wsData.push([]); // empty row for spacing

    // --- Loop through each bank section ---
    Object.keys(groupedData).forEach((acct_name) => {
      // Bank Name as a separate row (merged)
      wsData.push([acct_name]);

      // Header row
      wsData.push(["Vchr No", "Date", "Payer/Payee", "Narration", "Debit Amount", "Credit Amount", "Closing Bal."]);

      // Add data rows
      groupedData[acct_name].forEach((e) => {
        const drAmt = e.dr_amt;
        const crAmt = e.cr_amt;
        const closeBal = e.close_bal;

        // const drAmt = e.dr_amt ?? 0;
        // const crAmt = e.cr_amt ?? 0;
        // const closeBal = e.close_bal ?? 0;

        wsData.push([
          e.vchr_no || "",
          e.vchr_date || "",
          e.chq_no || "",
          e.narration || "",
          formatCurrency(drAmt),
          formatCurrency(crAmt),
          closeBal,
        ]);
      });

      // Empty row after each bank
      wsData.push([]);
    });

    // --- Convert to worksheet ---
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // --- Styling ---

    const range = XLSX.utils.decode_range(ws['!ref']);

    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cell_address = { r: R, c: C };
        const cell_ref = XLSX.utils.encode_cell(cell_address);
        const cell = ws[cell_ref];

        if (!cell) continue;

        // Bank Name row styling (first column of row with length 1)
        if (cell.v && wsData[R].length === 1) {
          if (R === 0 || R === 2) {
            cell.s = {
              font: { bold: true, sz: 12 },
              alignment: { horizontal: "center" },
           //   fill: { fgColor: { rgb: "DCE6F1" } }, // light blue
            };
          } else {
            cell.s = {
              font: { bold: true, sz: 12 },
              alignment: { horizontal: "left" },
           //   fill: { fgColor: { rgb: "DCE6F1" } }, // light blue
            };
          }

          // Merge across all 7 columns
          if (!ws['!merges']) ws['!merges'] = [];
          ws['!merges'].push({
            s: { r: R, c: 0 },
            e: { r: R, c: 6 },
          });
        }

        // Header row styling
        if (R > 0 && wsData[R][0] === "Vchr No") {
          cell.s = {
            font: { bold: true,}, // color: { rgb: "FFFFFF" } },
            alignment: { horizontal: "center" },
           // fill: { fgColor: { rgb: "2980B9" } }, // dark blue
           fill: { fgColor: { rgb: "E6E6E6" } },           
          };
        }

        // Bold for Opening/Closing Balance rows
        if (cell.v && typeof cell.v === "string") {
          const val = cell.v.toLowerCase();
          if (val.includes("opening balance") || val.includes("closing balance")) {
            if (val.includes("closing balance")) {
              cell.s = {
                font: { bold: true },
               // fill: { fgColor: { rgb: "E6E6E6" } }, // light grey
              };
            } else if (val.includes("opening balance")) {
              cell.s = {
                font: { bold: true }, // no fill for opening
              };
            }
          }
        }

        if ([4, 5, 6].includes(C)) {
          cell.s = {
            ...(cell.s || {}),
            alignment: { horizontal: "right" },
          };
        }
        
      }
    }

    // --- Set column widths ---
    ws['!cols'] = [
      { wch: 10 }, // Vchr No
      { wch: 12 }, // Date
      { wch: 25 }, // Payer/Payee
      { wch: 40 }, // Narration
      { wch: 15 }, // Debit Amount
      { wch: 15 }, // Credit Amount
      { wch: 18 }, // Closing Bal.
    ];

    // --- Add worksheet to workbook ---
    XLSX.utils.book_append_sheet(wb, ws, "Cash Bank Book");

    // --- Save workbook ---
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), `Ledger_Report_${formatDate(fromDate)}_To_${formatDate(toDate)}.xlsx`);

};
